// ============================================================================
// AUTO-GIFT PRODUCT HANDLER
// Save this as: assets/auto-gift.js
// ============================================================================

// Configuration - Update these values according to your needs
const GIFT_CONFIG = {
  productId: '17501458464814', 
  threshold: 10000, 
  giftProductHandle: 'gift-card-25',
  startDate: '2025-12-01T00:00:00-04:00', 
  endDate: '2025-12-31T23:59:59-04:00'    
};

function getESTDate() {
  // build a string representing the current time in America/New_York
  const estString = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date());

  // Convert into a Date object by reconstructing UTC
  // estString example: "10/31/2024, 09:32:10"
  const [month, day, year, hour, minute, second] = estString.match(/\d+/g);
  return new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}-04:00`);
}

function isPromotionActive() {
  const nowEST = getESTDate();
  const startDate = new Date(GIFT_CONFIG.startDate);
  const endDate = new Date(GIFT_CONFIG.endDate);

  const isActive = nowEST >= startDate && nowEST <= endDate;

  if (!isActive) {
    console.log('⏸️ Auto-gift promotion is not active', {
      currentEST: nowEST.toISOString(),
      start: startDate.toISOString(),
      end: endDate.toISOString()
    });
  }
  
  return isActive;
}


/**
 * Check if gift product is already in cart
 * @param {Array} cartItems - Array of cart items
 * @returns {boolean}
 */
function isGiftProductInCart(cartItems) {
  return cartItems.some(item => 
    item.variant_id.toString() === GIFT_CONFIG.productId.toString() ||
    item.handle === GIFT_CONFIG.giftProductHandle ||
    item.product_handle === GIFT_CONFIG.giftProductHandle
  );
}

/**
 * Calculate cart total excluding the gift product
 * @param {Array} cartItems - Array of cart items
 * @returns {number} - Total in cents
 */
function calculateCartTotalWithoutGift(cartItems) {
  return cartItems.reduce((total, item) => {
    // Skip gift product in calculation
    if (item.variant_id.toString() === GIFT_CONFIG.productId.toString() || 
        item.handle === GIFT_CONFIG.giftProductHandle ||
        item.product_handle === GIFT_CONFIG.giftProductHandle) {
      return total;
    }
    return total + item.final_line_price;
  }, 0);
}

/**
 * Get sections to bundle for cart updates
 * @returns {Array}
 */
function getBundledSections() {
  let sectionsToBundle = [];
  document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { 
    bubbles: true, 
    detail: { sections: sectionsToBundle } 
  }));
  return sectionsToBundle;
}

/**
 * Add gift product to cart
 * @returns {Promise}
 */
async function addGiftProductToCart() {
  try {
    console.log('🎁 Adding gift product to cart...');
    
    // Get sections that need to be updated
    const sections = getBundledSections();
    
    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: GIFT_CONFIG.productId,
        quantity: 1,
        properties: {
          '_gift_product': 'true',
          '_auto_added': 'true'
        },
        sections: sections.join(','),
        sections_url: window.location.pathname
      })
    });

    if (response.ok) {
      const result = await response.json();
      console.log('✅ Gift product added successfully');
      return result;
    } else {
      const error = await response.json();
      console.error('❌ Failed to add gift product:', error);
      return null;
    }
  } catch (error) {
    console.error('❌ Error adding gift product:', error);
    return null;
  }
}

/**
 * Remove gift product from cart
 * @param {string} lineKey - Cart line item key
 * @returns {Promise}
 */
async function removeGiftProductFromCart(lineKey) {
  try {
    console.log('🗑️ Removing gift product from cart...');
    
    // Get sections that need to be updated
    const sections = getBundledSections();
    
    const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
        sections: sections.join(','),
        sections_url: window.location.pathname
      })
    });

    if (response.ok) {
      const result = await response.json();
      console.log('✅ Gift product removed successfully');
      return result;
    } else {
      const error = await response.json();
      console.error('❌ Failed to remove gift product:', error);
      return null;
    }
  } catch (error) {
    console.error('❌ Error removing gift product:', error);
    return null;
  }
}

/**
 * Main function to handle auto-adding/removing gift product
 * @param {Object} cart - Current cart object
 * @returns {Promise<Object|null>} - Updated cart or null if no changes
 */
async function handleAutoGiftProduct(cart) {
  // Check if promotion is active
  if (!isPromotionActive()) {
    // If promotion is not active but gift product is in cart, remove it
    if (cart && cart.items && isGiftProductInCart(cart.items)) {
      console.log('⚠️ Promotion ended, removing gift product...');
      const giftLineItem = cart.items.find(item => 
        item.variant_id.toString() === GIFT_CONFIG.productId.toString() ||
        item.handle === GIFT_CONFIG.giftProductHandle ||
        item.product_handle === GIFT_CONFIG.giftProductHandle
      );
      
      if (giftLineItem) {
        const removeResult = await removeGiftProductFromCart(giftLineItem.key);
        if (removeResult) {
          const updatedCartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
          const updatedCart = await updatedCartResponse.json();
          if (removeResult.sections) {
            updatedCart.sections = removeResult.sections;
          }
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'auto-gift:remove',
              cart: updatedCart
            }
          }));
          return updatedCart;
        }
      }
    }
    return null;
  }

  if (!cart || !cart.items) {
    console.error('❌ Invalid cart object');
    return null;
  }

  const cartItems = cart.items;
  const hasGiftProduct = isGiftProductInCart(cartItems);
  const cartTotal = calculateCartTotalWithoutGift(cartItems);
  const meetsThreshold = cartTotal >= GIFT_CONFIG.threshold;

  console.log('🔍 Auto-gift check:', {
    cartTotal: (cartTotal / 100).toFixed(2) + '$',
    threshold: (GIFT_CONFIG.threshold / 100).toFixed(2) + '$',
    hasGiftProduct,
    meetsThreshold,
    promotionActive: true,
    action: meetsThreshold && !hasGiftProduct ? 'ADD' : !meetsThreshold && hasGiftProduct ? 'REMOVE' : 'NONE'
  });

  // Case 1: Cart total >= threshold and no gift product → Add it
  if (meetsThreshold && !hasGiftProduct) {
    const addResult = await addGiftProductToCart();
    
    if (addResult) {
      // Fetch updated cart with all data
      const updatedCartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
      const updatedCart = await updatedCartResponse.json();
      
      // Merge sections data if available
      if (addResult.sections) {
        updatedCart.sections = addResult.sections;
      }
      
      // Dispatch cart change event with full data
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'auto-gift:add',
          cart: updatedCart
        }
      }));
      
      return updatedCart;
    }
  }
  
  // Case 2: Cart total < threshold and has gift product → Remove it
  if (!meetsThreshold && hasGiftProduct) {
    // Find the gift product line item
    const giftLineItem = cartItems.find(item => 
      item.variant_id.toString() === GIFT_CONFIG.productId.toString() ||
      item.handle === GIFT_CONFIG.giftProductHandle ||
      item.product_handle === GIFT_CONFIG.giftProductHandle
    );
    
    if (giftLineItem) {
      const removeResult = await removeGiftProductFromCart(giftLineItem.key);
      
      if (removeResult) {
        // Fetch updated cart with all data
        const updatedCartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
        const updatedCart = await updatedCartResponse.json();
        
        // Merge sections data if available
        if (removeResult.sections) {
          updatedCart.sections = removeResult.sections;
        }
        
        // Dispatch cart change event with full data
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'auto-gift:remove',
            cart: updatedCart
          }
        }));
        
        return updatedCart;
      }
    }
  }

  return null;
}

/**
 * Initialize auto-gift functionality
 * This should be called when the page loads
 */
async function initAutoGift() {
  try {
    console.log('🚀 Initializing auto-gift system...');
    
    // Check if promotion is active first
    if (!isPromotionActive()) {
      console.log('⏸️ Promotion not active, skipping initialization');
      // Still check cart to remove gift if present
      const cart = await fetch(`${window.Shopify.routes.root}cart.js`).then(r => r.json());
      await handleAutoGiftProduct(cart);
      return;
    }
    
    // Get current cart
    const cart = await fetch(`${window.Shopify.routes.root}cart.js`).then(r => r.json());
    
    // Check and handle gift product
    await handleAutoGiftProduct(cart);
  } catch (error) {
    console.error('❌ Error initializing auto-gift:', error);
  }
}

// Export functions for use in theme
if (typeof window !== 'undefined') {
  window.AutoGift = {
    handle: handleAutoGiftProduct,
    init: initAutoGift,
    config: GIFT_CONFIG,
    isActive: isPromotionActive
  };  
}